import { Problem, ProblemDetectionContext } from '../types';
import { detectQueueDurationIssues } from './queue-duration-detector';
import { detectLexErrors } from './lex-error-detector';
import { detectFlowExecutionFailures } from './flow-execution-detector';
import { detectWisdomIssues } from './wisdom-detector';
import { detectRepeatedInputFailures } from './input-failure-detector';
import { detectInputTimeouts } from './input-timeout-detector';
import { detectLexFallbackIntents } from './lex-fallback-detector';
import { detectExcessiveHoldTime } from './hold-time-detector';
import { detectLambdaInvocationIssues } from './lambda-detector';
import { detectRepeatedMessagePlayback } from './message-playback-detector';
import { detectCustomerDisconnects } from './disconnect-detector';
import { detectFlowModuleFailures } from './flow-module-failure-detector';
import { detectFlowExecutionDelays } from './flow-execution-delay-detector';
import { detectFlowBlockDurations } from './flow-block-duration-detector';

export class ProblemDetector {
  private static generateId(): string {
    return Math.random().toString(36).substring(2, 11);
  }

  private static isValidLogEntry(log: unknown): boolean {
    // Check if log has the expected structure of InteractionLogEntry (interaction logs)
    return log !== null && typeof log === 'object' && 'EventType' in log && 'LogData' in log && 'AbsoluteTime' in log;
  }

  static detectProblems(context: ProblemDetectionContext): Problem[] {
    const problems: Problem[] = [];

    // Handle Interaction Log problems
    if (context.logs && context.logs.length > 0) {
      // Validate that all logs are proper InteractionLogEntry objects
      const validLogs = context.logs.filter(this.isValidLogEntry);

      if (validLogs.length > 0) {
        // Create context with validated logs
        const validContext = { ...context, logs: validLogs };

        // Detect various types of problems using validated context
        problems.push(...detectQueueDurationIssues(validContext, this.generateId));
        problems.push(...detectLexErrors(validContext, this.generateId));
        problems.push(...detectFlowExecutionFailures(validContext, this.generateId));
        problems.push(...detectWisdomIssues(validContext, this.generateId));
        problems.push(...detectRepeatedInputFailures(validContext, this.generateId));
        problems.push(...detectInputTimeouts(validContext, this.generateId));
        problems.push(...detectLexFallbackIntents(validContext, this.generateId));
        problems.push(...detectExcessiveHoldTime(validContext, this.generateId));
        problems.push(...detectLambdaInvocationIssues(validContext, this.generateId));
        problems.push(...detectRepeatedMessagePlayback(validContext, this.generateId));
        problems.push(...detectCustomerDisconnects(validContext, this.generateId));
      }
    }

    // Handle Flow Log problems
    if (context.flowLogs && context.flowLogs.length > 0) {
      problems.push(...detectFlowModuleFailures(context, this.generateId));
      problems.push(...detectFlowExecutionDelays(context, this.generateId));
      problems.push(...detectFlowBlockDurations(context, this.generateId));
    }

    // Sort by severity and timestamp
    return problems.sort((a, b) => {
      const severityOrder = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
      if (severityOrder[a.severity] !== severityOrder[b.severity]) {
        return severityOrder[b.severity] - severityOrder[a.severity];
      }
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }
}
